import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Text } from '@hubdevs/viu-ui-shared';
import { useNavigate, useParams } from "react-router-dom";
import { Button } from '@mui/material';
import { AppPage } from "../../components/Page/Page";
import { AppFormRow } from "../../components/AppFormRow/AppFormRow";
import { AppFormCell } from "../../components/AppFormCell/AppFormCell";
import { ApiServiceContext } from "../../api/ApiService.context";
import { QuoteExperienceBase } from "./sections/QuoteExperienceBase.section";
import { setByPointer, getByPointer } from "json-pointer-relational";
import { QuoteExperiencePanels } from "./sections/QuoteExperiencePanels.section";
import { QuoteExperienceService } from "../../api/quote-experience/QuoteExperienceService.class";
import { BundleService } from "../../api/bundle/BundleService.class";
import { AttributionService } from "../../api/attribution/AttributionService.class";
import { BundleAttribution } from "../../api/bundle/types";
import { OrganizationService } from "../../api/organization/OrganizationService.class";

export type QEImage = {
    id: string;
    file?: File | Blob;
    name: string;
    previewUrl?: string | ArrayBuffer | undefined;
    error?: string | null;
    path?: string;
    hint?: string;
};

export type QEFile = {
    id: string;
    file?: File;
    name: string;
    path?: string;
    error?: string | null;
};

export type QuoteExperienceState = {
    id?: string;
    displayName: string;
    partnerAttribution: string | undefined;
    formDataByType: Record<string, Record<string, any>>;
    images: QEImage[];
    pendingFiles: QEFile[];
    logos: QEImage[];
    favicon: QEImage[];
    icons: QEImage[];
    schemaVersionByType: Record<string, number>;
    attrGuidByType: Record<string, string>;
}

export const QuoteExperiencePageQuickSetup: FC<{}> = () => {
    const { getApiServiceCollections } = useContext(ApiServiceContext);
    const { AttributionService, BundleService, QuoteExperienceService, OrganizationService } = getApiServiceCollections() as {
        AttributionService: AttributionService,
        BundleService: BundleService,
        QuoteExperienceService: QuoteExperienceService;
        OrganizationService: OrganizationService;
    };
    const navigate = useNavigate();
    const { organizationGuid, quoteExperienceBundleGuid } = useParams();
    const [currentBundleGuid, setCurrentBundleGuid] = useState<string | undefined>(quoteExperienceBundleGuid);
    const [saveErrorsByType, setSaveErrorsByType] = useState<Record<string, string>>({});
    const [isLoadingByKey, _setIsLoadingByKey] = useState<Record<string, boolean>>({});
    const [canSaveBySection, setCanSaveBySection] = useState<Record<string, string | null>>({});
    const [saveBundleError, setSaveBundleError] = useState<string>('');

    const generalSaveErrors = useMemo(() => Object.values(canSaveBySection).filter(canSave => canSave !== null), [canSaveBySection]);
    const canSave = useMemo(() => generalSaveErrors.length === 0, [generalSaveErrors]);

    const [initialQuoteExperienceData, setInitialQuoteExperienceData] = useState<QuoteExperienceState>({
        displayName: '',
        partnerAttribution: undefined,
        formDataByType: {},
        images: [],
        pendingFiles: [],
        logos: [],
        favicon: [],
        icons: [],
        schemaVersionByType: {},
        attrGuidByType: {}
    });
    const [quoteExperienceData, setQuoteExperienceData] = useState<QuoteExperienceState>({
        displayName: '',
        partnerAttribution: undefined,
        formDataByType: {},
        images: [],
        pendingFiles: [],
        logos: [],
        favicon: [],
        icons: [],
        schemaVersionByType: {},
        attrGuidByType: {}
    });

    const updateData = useCallback((pointer: string, value: any, setInitial?: boolean, skipCurrent?: boolean) => {
        const resolve = (val: any, obj: Record<string, any>) => {
            if (typeof val === 'function') {
                try { return val(getByPointer(pointer, obj)); } catch { return val(undefined); }
            }
            return val;
        };
        if (!skipCurrent) {
            setQuoteExperienceData(prev => {
                const nData = { ...prev };
                try {
                    setByPointer(resolve(value, nData), pointer, nData);
                } catch (e) {
                    console.error(e);
                    return prev;
                }
                return nData;
            });
        }
        if (setInitial) {
            setInitialQuoteExperienceData(prev => {
                const nData = { ...prev };
                try {
                    setByPointer(resolve(value, nData), pointer, nData);
                } catch (e) {
                    console.error(e);
                    return prev;
                }
                return nData;
            });
        }
    }, []);

    const setIsLoadingByKey = useCallback((key: string, value: boolean) => {
        _setIsLoadingByKey(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const isLoading = useMemo(() =>
        Object.values(isLoadingByKey).find(val => val)
        , [isLoadingByKey]);

    const handleCanSaveBySection = (section: string, canSave: string | null) =>
        setCanSaveBySection(prev => {
            if (prev[section] === canSave) return prev;
            return { ...prev, [section]: canSave };
        });

    const saveFormData = useCallback(async (bundleGuid: string): Promise<Record<string, any>> => {
        const formDataList = Object.entries(quoteExperienceData.formDataByType);
        const saveFormDataResponse: Record<string, any> = {
            updateData: {},
            saveErrorsByType: {},
            error: false
        };

        for (let i = 0; i < formDataList.length; i++) {
            const schemaType = formDataList[i][0];
            const formData = formDataList[i][1];
            const existingGuid = quoteExperienceData.attrGuidByType[schemaType];

            try {
                if (existingGuid) {
                    await AttributionService.updateAttribution({
                        attributionGuid: existingGuid,
                        status: 'ACTIVE',
                        properties: formData,
                        typeVersion: quoteExperienceData.schemaVersionByType[schemaType] || undefined
                    },
                        { AttributionService }
                    );
                } else {
                    const postAttribution = AttributionService.createAttribution;
                    const aResp = await postAttribution({
                        displayName: `QE: ${quoteExperienceData.displayName} - ${schemaType}`,
                        status: 'ACTIVE',
                        type: schemaType,
                        properties: formData,
                        typeVersion: quoteExperienceData.schemaVersionByType[schemaType] || undefined
                    });
                    const attributionGuid = aResp?.textBody;
                    if (attributionGuid) {
                        saveFormDataResponse.updateData[`/attrGuidByType/${schemaType}`] = attributionGuid;
                        // Link to organization
                        const postOrgAttr = OrganizationService.createOrganizationAttribution;
                        const oaResp = await postOrgAttr({
                            organizationGuid,
                            attributionGuid,
                            required: false
                        });
                        const organizationAttributionGuid = oaResp?.textBody;
                        if (organizationAttributionGuid) {
                            // Link to bundle
                            const postBundleAttr = BundleService.addBundleAttribution;
                            await postBundleAttr({
                                bundleGuid,
                                organizationAttributionGuid
                            });
                        }
                    }
                }
                saveFormDataResponse.saveErrorsByType[schemaType] = '';
            } catch (err: any) {
                saveFormDataResponse.saveErrorsByType[schemaType] = 'Failed to save attribution';
                saveFormDataResponse.error = true;
                return saveFormDataResponse;
            }
        }
        return saveFormDataResponse;
    }, [quoteExperienceData, AttributionService, OrganizationService, BundleService, organizationGuid]);

    const handlePartnerAttribution = useCallback(async (
        bundleGuid: string,
        existingPartnerAttributionGuid?: string,
        selectedPartnerAttributionGuid?: string,
    ): Promise<void> => {
        if (existingPartnerAttributionGuid === selectedPartnerAttributionGuid) {
            return;
        }

        const bundleAttrs: BundleAttribution[] = [];
        let bundleAttrsResp = await BundleService.getAttributions(bundleGuid);
        while (bundleAttrsResp.retry) {
            bundleAttrsResp = await bundleAttrsResp.retry.catch(error => error);
        }
        if (bundleAttrsResp.error) {
            // TODO - Error out in some way
            console.warn('Failed to load existing Bundle Attributions for Quote Experience. Exiting');
            return
        }

        // Remove existing
        if (existingPartnerAttributionGuid && existingPartnerAttributionGuid !== selectedPartnerAttributionGuid) {
            const orgAttr = bundleAttrs.find((ba: any) => ba.attributionGuid === existingPartnerAttributionGuid);
            if (orgAttr) {
                const deleteEndpoint = BundleService.deleteBundleAttribution;
                await deleteEndpoint({
                    bundleGuid,
                    organizationAttributionGuid: orgAttr.organizationAttributionGuid
                });
            }
        }

        // Add new
        if (selectedPartnerAttributionGuid) {
            // First check bundle attrs, then fall back to organization attrs
            let orgAttr = bundleAttrs.find((ba: any) => ba.attributionGuid === selectedPartnerAttributionGuid);
            if (!orgAttr) {
                const getOrgAttrs = OrganizationService.getOrganizationAttributions;
                let orgResp = await getOrgAttrs({ organizationGuid, skip: 0, limit: 1000 });
                while (orgResp.retry) {
                    orgResp = await orgResp.retry.catch(error => error);
                }
                if (orgResp.error) {
                    // TODO - Error out in some way
                    console.warn('Failed to load Organization Attributions when setting partner attribution on Quote Experience. Exiting');
                    return;
                }
                const orgAttrs = orgResp.jsonBody!.data ?? [];
                orgAttr = orgAttrs.find((oa: any) => oa.attributionGuid === selectedPartnerAttributionGuid);
            }
            if (orgAttr) {
                const postBundleAttr = BundleService.addBundleAttribution;
                await postBundleAttr({
                    bundleGuid,
                    organizationAttributionGuid: orgAttr.organizationAttributionGuid
                });
            }
        }
    }, [BundleService, OrganizationService, organizationGuid]);

    const processSave = useCallback(async () => {
        let processSaveReturn: Record<string, any> = {
            saveBundleError: '',
            currentBundleGuid,
            updateData: {},
            error: false
        };
        // SAVE BUNDLE
        let bundleGuid = currentBundleGuid;
        if (!bundleGuid) {
            let bundleGuidResp;
            try {
                bundleGuidResp = await BundleService.createBundle({
                    type: 'QUOTE_EXPERIENCE',
                    displayName: quoteExperienceData.displayName,
                    organizationGuid
                });
            } catch (err: any) {
                processSaveReturn['saveBundleError'] = err?.status === 409
                    ? 'A Quote Experience with this name already exists. Please choose a different name.'
                    : 'Failed to save Quote Experience Bundle. Please check the server status and try again.';
                processSaveReturn['error'] = true;
                return processSaveReturn;
            }
            if (!bundleGuidResp?.textBody) {
                processSaveReturn['saveBundleError'] = 'Failed to save Quote Experience Bundle. Please check the server status and try again.';
                processSaveReturn['error'] = true;
                return processSaveReturn;
            }
            bundleGuid = bundleGuidResp!.textBody!;
            processSaveReturn['currentBundleGuid'] = bundleGuid;
        }

        // SAVE IMAGES
        await Promise.all(quoteExperienceData.images.map(async (image, idx) => {
            if (!image.file || !image.path) {
                if (image.name || image.file) {
                    processSaveReturn.updateData[`/images/${idx}/error`] = !image.file ? 'Please choose a file.' : 'Please provide an image name.';
                    processSaveReturn['error'] = true;
                }
                return;
            }
            await QuoteExperienceService.uploadFile({
                bundleGuid,
                filepath: image.path,
                file: image.file as File
            })
                .then(() => {
                    processSaveReturn.updateData[`/images/${idx}/error`] = null;
                })
                .catch(() => {
                    processSaveReturn.updateData[`/images/${idx}/error`] = 'Failed to upload image';
                    processSaveReturn['error'] = true;
                });
        }));

        // SAVE LOGOS
        await Promise.all(quoteExperienceData.logos.map(async (image, idx) => {
            if (!image.file || !image.path) {
                if (image.name || image.file) {
                    processSaveReturn.updateData[`/logos/${idx}/error`] = !image.file ? 'Please choose a file.' : 'Please provide an image name.';
                    processSaveReturn['error'] = true;
                }
                return;
            }
            function toDataURL(url: string, callback: (dataUrl: string | ArrayBuffer | null) => void) {
                var xhr = new XMLHttpRequest();
                xhr.onload = function () {
                    var reader = new FileReader();
                    reader.onloadend = function () {
                        callback(reader.result);
                    }
                    reader.readAsDataURL(xhr.response);
                };
                xhr.open('GET', url);
                xhr.responseType = 'blob';
                xhr.send();
            }

            toDataURL(URL.createObjectURL(image.file), function (dataUrl) {
                console.log('Uploading image:', dataUrl)
            });

            await QuoteExperienceService.uploadFile({
                bundleGuid,
                filepath: image.path,
                file: image.file as File
            })
                .then(() => {
                    processSaveReturn.updateData[`/logos/${idx}/error`] = null;
                })
                .catch(() => {
                    processSaveReturn.updateData[`/logos/${idx}/error`] = 'Failed to upload image';
                    processSaveReturn['error'] = true;
                });
        }));

        // SAVE FAVICONS
        await Promise.all(quoteExperienceData.favicon.map(async (image, idx) => {
            if (!image.file || !image.path) {
                if (image.name || image.file) {
                    processSaveReturn.updateData[`/favicon/${idx}/error`] = !image.file ? 'Please choose a file.' : 'Please provide an image name.';
                    processSaveReturn['error'] = true;
                }
                return;
            }
            await QuoteExperienceService.uploadFile({
                bundleGuid,
                filepath: image.path,
                file: image.file as File
            })
                .then(() => {
                    processSaveReturn.updateData[`/favicon/${idx}/error`] = null;
                })
                .catch(() => {
                    processSaveReturn.updateData[`/favicon/${idx}/error`] = 'Failed to upload image';
                    processSaveReturn['error'] = true;
                });
        }));

        // SAVE ICONS
        await Promise.all(quoteExperienceData.icons.map(async (image, idx) => {
            if (!image.file || !image.path) {
                if (image.name || image.file) {
                    processSaveReturn.updateData[`/icons/${idx}/error`] = !image.file ? 'Please choose a file.' : 'Please provide an image name.';
                    processSaveReturn['error'] = true;
                }
                return;
            }
            await QuoteExperienceService.uploadFile({
                bundleGuid,
                filepath: image.path,
                file: image.file as File
            })
                .then(() => {
                    processSaveReturn.updateData[`/icons/${idx}/error`] = null;
                })
                .catch(() => {
                    processSaveReturn.updateData[`/icons/${idx}/error`] = 'Failed to upload image';
                    processSaveReturn['error'] = true;
                });
        }));

        // SAVE FILES (File Manager)
        await Promise.all(quoteExperienceData.pendingFiles.map(async (pendingFile, idx) => {
            if (!pendingFile.file || !pendingFile.path) {
                return;
            }
            await QuoteExperienceService.uploadFile({
                bundleGuid,
                filepath: pendingFile.path,
                file: pendingFile.file
            })
                .then(() => {
                    processSaveReturn.updateData[`/pendingFiles/${idx}/error`] = null;
                })
                .catch(() => {
                    processSaveReturn.updateData[`/pendingFiles/${idx}/error`] = 'Failed to upload file';
                    processSaveReturn['error'] = true;
                });
        }));

        // Handle partner attribution
        try {
            await handlePartnerAttribution(
                bundleGuid,
                initialQuoteExperienceData.partnerAttribution,
                quoteExperienceData.partnerAttribution,
            );
        } catch (e) {
            processSaveReturn['saveBundleError'] = 'Failed to save Quote Experience Partner Attribution. Please check the server status and try again.';
            processSaveReturn['error'] = true;
            return processSaveReturn;
        }

        // Save form data
        const saveFormDataResponse = await saveFormData(bundleGuid);
        processSaveReturn = {
            ...processSaveReturn,
            ...saveFormDataResponse
        }
        if (processSaveReturn.error) {
            return processSaveReturn; // TODO
        }

        // Publish
        const publishResp = await QuoteExperienceService.publish({ bundleGuid });
        if (publishResp?.error) {
            processSaveReturn.error = true;
            processSaveReturn['saveBundleError'] = 'Failed to publish to CDN. Please check the server status and try again.';
        }
        return processSaveReturn;
    }, [QuoteExperienceService, handlePartnerAttribution, saveFormData, currentBundleGuid, BundleService, quoteExperienceData,
        initialQuoteExperienceData, organizationGuid]);

    const handleSave = useCallback(async () => {
        setSaveErrorsByType({});
        setSaveBundleError('');
        setIsLoadingByKey('save', true);
        // TODO
        const processSaveResults = await processSave();
        setSaveBundleError(processSaveResults['saveBundleError']);
        setCurrentBundleGuid(processSaveResults['currentBundleGuid']);
        setSaveErrorsByType(processSaveResults['saveErrorsByType']);
        updateData('/', (allData: Record<string, any>) => {
            const nData: Record<string, any> = { ...allData };
            Object.entries(processSaveResults.updateData).forEach(([pointer, value]) => {
                setByPointer(value, pointer, nData);
            });
            return nData;
        });
        setIsLoadingByKey('save', false);
        if (!processSaveResults.error) {
            navigate(`/organization/${organizationGuid}`);
        }
    }, [processSave, updateData, navigate, organizationGuid, setIsLoadingByKey]);

    const saveErrorsList = useMemo(() => {
        return Object.entries(saveErrorsByType || {}).filter(([key, error]) => !!error).map(([key, error]) => `${key} - ${error}`)
    }, [saveErrorsByType]);

    return (
        <AppPage
            title={!quoteExperienceBundleGuid ? 'Add Quote Experience' : 'Edit Quote Experience'}
            isLoading={isLoading}
            buttonRowContent={
                <>
                    {Object.keys(saveErrorsList).length > 0 && (
                        <>
                            <AppFormRow>
                                <Text color="error" style={{ color: 'red', marginBottom: 12, marginLeft: 12 }}>
                                    Some forms failed to save. Please fix the errors above and try again.
                                </Text>
                            </AppFormRow>
                            {
                                saveErrorsList.map(error => (
                                    <AppFormRow>
                                        <Text color="error" style={{ color: 'red', marginBottom: 12, marginLeft: 12 }}>
                                            {error}
                                        </Text>
                                    </AppFormRow>
                                ))
                            }
                        </>
                    )}
                    {
                        generalSaveErrors.map(saveError => (
                            <AppFormRow>
                                <Text color="error" style={{ color: 'red', marginBottom: 12, marginLeft: 12 }}>
                                    {saveError}
                                </Text>
                            </AppFormRow>
                        ))
                    }
                    {
                        saveBundleError
                            ? (
                                <AppFormRow>
                                    <Text color="error" style={{ color: 'red', marginBottom: 12, marginLeft: 12 }}>
                                        {saveBundleError}
                                    </Text>
                                </AppFormRow>
                            )
                            : null
                    }
                    <AppFormCell>
                        <Button variant="outlined" onClick={() => navigate(`/organization/${organizationGuid}`)}>
                            Cancel
                        </Button>
                    </AppFormCell>
                    <AppFormCell>
                        <Button
                            variant="outlined"
                            disabled={isLoading || !canSave}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </AppFormCell>
                </>
            }
        >
            <QuoteExperienceBase
                organizationGuid={organizationGuid!}
                bundleGuid={quoteExperienceBundleGuid}
                initial={initialQuoteExperienceData}
                setIsLoading={setIsLoadingByKey}
                updateData={updateData}
                setCanSaveBySection={handleCanSaveBySection}
            />
            <QuoteExperiencePanels
                organizationGuid={organizationGuid!}
                bundleGuid={quoteExperienceBundleGuid}
                initial={initialQuoteExperienceData}
                current={quoteExperienceData}
                setIsLoading={setIsLoadingByKey}
                updateData={updateData}
                setCanSaveBySection={handleCanSaveBySection}
            />
        </AppPage>
    );
}